import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const ok = current.length >= 1 && next.length >= 6 && next === confirm && current !== next;

  const submit = async () => {
    if (!ok) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("تم تحديث كلمة المرور");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-cream-200 card-shadow" data-testid="change-password-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 grid place-items-center text-slate-700">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">كلمة المرور</h3>
            <p className="text-xs text-slate-500 mt-0.5">يُفضّل تحديثها بشكل دوري.</p>
          </div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="cp-toggle">
          {open ? "إلغاء" : "تغيير"}
        </button>
      </div>

      {open && (
        <div className="mt-5 space-y-3">
          <Row label="كلمة المرور الحالية" value={current} onChange={setCurrent} show={show} setShow={setShow} testid="cp-current" />
          <Row label="كلمة المرور الجديدة" value={next} onChange={setNext} show={show} setShow={setShow} testid="cp-new" />
          <Row label="تأكيد كلمة المرور الجديدة" value={confirm} onChange={setConfirm} show={show} setShow={setShow} testid="cp-confirm" />
          <ul className="text-xs text-slate-500 grid gap-1 mt-2">
            <li className={next.length >= 6 ? "text-emerald-700" : ""}>• 6 أحرف على الأقل</li>
            <li className={next && next !== current ? "text-emerald-700" : ""}>• تختلف عن الحالية</li>
            <li className={confirm && next === confirm ? "text-emerald-700" : ""}>• تطابق التأكيد</li>
          </ul>
          <button onClick={submit} disabled={!ok || saving} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60 mt-2" data-testid="cp-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            تحديث كلمة المرور
          </button>
        </div>
      )}
    </section>
  );
}

function Row({ label, value, onChange, show, setShow, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 pe-11 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
          data-testid={testid}
        />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 end-3 my-auto h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-teal-700">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
